import { createSlice } from "@reduxjs/toolkit";
import { pushDynamicTab } from "../utils/dynamicTabs";

const SLICE_VERSION = "1.0";

const getAllTabs = (state) => [...state.defaultTabs, ...state.dynamicTabs];

// Record the tab the user is leaving so a later tab closure can return to it.
const pushTabHistory = (state, tabId) => {
  if (!tabId) return;
  state.tabHistory = state.tabHistory.filter((id) => id !== tabId);
  state.tabHistory.push(tabId);
};

export const initialState = {
  _version: SLICE_VERSION,
  defaultTabs: [{ id: "claims-list", name: "Claims List" }],
  dynamicTabs: [],
  activeTab: 0,
  // Stack of previously active tab ids, most-recent-last — lets removeTab send the
  // user back to whichever tab/page they were actually viewing before, not tab 0.
  tabHistory: [],
};

const claimsSlice = createSlice({
  name: "claims",
  initialState,
  reducers: {
    addTab: (state, action) => {
      const tabDetails = action.payload;
      const prevActiveId = getAllTabs(state)[state.activeTab]?.id;
      const added = pushDynamicTab(state, tabDetails, state.defaultTabs.length);
      if (added) pushTabHistory(state, prevActiveId);
    },

    removeTab: (state, action) => {
      const { tabIndex } = action.payload;
      if (tabIndex < state.defaultTabs.length) return;

      const tabsBefore = getAllTabs(state);
      const removedId = tabsBefore[tabIndex]?.id;
      const activeId = tabsBefore[state.activeTab]?.id;
      const wasActive = tabIndex === state.activeTab;

      const dynamicIndex = tabIndex - state.defaultTabs.length;
      state.dynamicTabs.splice(dynamicIndex, 1);
      state.tabHistory = state.tabHistory.filter((id) => id !== removedId);

      const tabsAfter = getAllTabs(state);

      if (!wasActive) {
        // The tab the user was viewing wasn't the one closed — stay on it, just
        // recompute its index since removing an earlier tab shifts the array.
        const newIndex = tabsAfter.findIndex((t) => t.id === activeId);
        state.activeTab = newIndex >= 0 ? newIndex : Math.min(state.activeTab, tabsAfter.length - 1);
        return;
      }

      // The active tab itself was closed — return to the last tab/page the user
      // was actually on, walking back through history until one still exists.
      while (state.tabHistory.length) {
        const candidateId = state.tabHistory.pop();
        const idx = tabsAfter.findIndex((t) => t.id === candidateId);
        if (idx >= 0) {
          state.activeTab = idx;
          return;
        }
      }

      // No usable history — fall back to the default Claims List tab.
      state.activeTab = 0;
    },

    setActiveTab: (state, action) => {
      const { index } = action.payload;
      const maxIndex = state.defaultTabs.length + state.dynamicTabs.length - 1;
      const nextIndex = Math.min(Math.max(index, 0), maxIndex);
      if (nextIndex !== state.activeTab) {
        pushTabHistory(state, getAllTabs(state)[state.activeTab]?.id);
        state.activeTab = nextIndex;
      }
    },

    resetTabs: (state) => {
      state.dynamicTabs = [];
      state.activeTab = 0;
      state.tabHistory = [];
    },
  },
});

export const { addTab, removeTab, setActiveTab, resetTabs } = claimsSlice.actions;
export default claimsSlice.reducer;
